/**
 * @fileoverview Tenant management hooks using TanStack Query.
 *
 * This module provides React hooks for managing tenant context including:
 * - Fetching current user's tenant membership
 * - Tenant profile and settings management
 * - Role-based permission checks
 * - Tenant user management
 *
 * All hooks use TanStack Query for server state management with automatic
 * caching, refetching, and cache invalidation.
 *
 * @module hooks/use-tenant
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// ============================================================================
// Types
// ============================================================================

export type TenantUserRole = "owner" | "manager" | "dispatcher" | "technician";

export interface TenantUser {
  id: string;
  tenant_id: string;
  auth_user_id: string;
  email: string;
  name: string;
  phone: string | null;
  avatar_url: string | null;
  role: TenantUserRole;
  permissions: Record<string, boolean>;
  is_active: boolean;
  invited_at: string | null;
  accepted_at: string | null;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  owner_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  industry: "plumbing" | "hvac" | "both" | null;
  timezone: string;
  service_area_description: string | null;
  onboarding_status: "pending" | "provisioning" | "active" | "paused" | "cancelled";
  subscription_status: "trial" | "active" | "past_due" | "cancelled";
  subscription_tier: "starter" | "receptionist" | "outbound" | "complete" | null;
  vapi_phone_number: string | null;
  vapi_assistant_id: string | null;
  config: Record<string, unknown>;
  onboarding_data: Record<string, unknown>;
  monthly_minutes_used?: number | null;
  site_status?: "dev" | "live" | "suspended";
  custom_domain?: string | null;
  setup_fee_paid?: boolean;
  setup_fee_paid_at?: string | null;
  go_live_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface TenantWithUser extends Tenant {
  tenant_user: TenantUser;
}

// ============================================================================
// Query Keys
// ============================================================================

export const TENANT_QUERY_KEY = ["tenant"];
export const TENANT_USER_QUERY_KEY = ["tenant", "user"];
export const TENANT_USERS_QUERY_KEY = ["tenant", "users"];

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetches the current user's tenant membership and tenant details.
 */
async function fetchCurrentTenant(userId: string): Promise<TenantWithUser | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from("tenant_users")
    .select(`
      *,
      tenant:tenants(*)
    `)
    .eq("auth_user_id", userId)
    .eq("is_active", true)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // No rows found - user not a member of any tenant
      return null;
    }
    throw error;
  }

  if (!data || !data.tenant) return null;

  return {
    ...data.tenant,
    tenant_user: {
      id: data.id,
      tenant_id: data.tenant_id,
      auth_user_id: data.auth_user_id,
      email: data.email,
      name: data.name,
      phone: data.phone,
      avatar_url: data.avatar_url,
      role: data.role,
      permissions: data.permissions || {},
      is_active: data.is_active,
      invited_at: data.invited_at,
      accepted_at: data.accepted_at,
      last_login_at: data.last_login_at,
      created_at: data.created_at,
      updated_at: data.updated_at,
    },
  };
}

/**
 * Fetches all users in the current tenant.
 */
async function fetchTenantUsers(tenantId: string): Promise<TenantUser[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("tenant_users")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("role", { ascending: true })
    .order("name", { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Updates tenant profile information.
 */
async function updateTenant(
  tenantId: string,
  updates: Partial<Omit<Tenant, "id" | "created_at" | "updated_at">>
): Promise<Tenant> {
  if (!supabase) throw new Error("Supabase client not initialized");

  const { data, error } = await supabase
    .from("tenants")
    .update(updates)
    .eq("id", tenantId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Invites a new user to the tenant.
 */
async function inviteUserToTenant(
  tenantId: string,
  invitation: {
    email: string;
    name: string;
    role: TenantUserRole;
    phone?: string;
  }
): Promise<TenantUser> {
  if (!supabase) throw new Error("Supabase client not initialized");

  // First check if user already exists in auth
  // For now, create a placeholder tenant_user record
  // The auth_user_id will be updated when the user accepts the invitation

  const { data, error } = await supabase
    .from("tenant_users")
    .insert({
      tenant_id: tenantId,
      email: invitation.email,
      name: invitation.name,
      role: invitation.role,
      phone: invitation.phone,
      invited_at: new Date().toISOString(),
      is_active: false, // Will be activated when user accepts
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates a tenant user's role or details.
 * Includes tenant_id filter for proper multi-tenant isolation.
 */
async function updateTenantUser(
  tenantId: string,
  userId: string,
  updates: Partial<Pick<TenantUser, "name" | "phone" | "role" | "permissions" | "is_active">>
): Promise<TenantUser> {
  if (!supabase) throw new Error("Supabase client not initialized");

  const { data, error } = await supabase
    .from("tenant_users")
    .update(updates)
    .eq("id", userId)
    .eq("tenant_id", tenantId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Removes a user from the tenant.
 * Includes tenant_id filter for proper multi-tenant isolation.
 */
async function removeUserFromTenant(tenantId: string, userId: string): Promise<void> {
  if (!supabase) throw new Error("Supabase client not initialized");

  const { error } = await supabase
    .from("tenant_users")
    .delete()
    .eq("id", userId)
    .eq("tenant_id", tenantId);

  if (error) throw error;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch the current user's tenant context.
 *
 * Returns the tenant and the user's membership details including role.
 * Returns null if user is not a member of any tenant.
 *
 * @example
 * ```tsx
 * function BusinessDashboard() {
 *   const { data: tenant, isLoading } = useCurrentTenant();
 *
 *   if (isLoading) return <Spinner />;
 *   if (!tenant) return <NoTenantAccess />;
 *
 *   return (
 *     <div>
 *       <h1>Welcome to {tenant.name}</h1>
 *       <p>Your role: {tenant.tenant_user.role}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useCurrentTenant() {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...TENANT_QUERY_KEY, user?.id],
    queryFn: () => fetchCurrentTenant(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch all users in the current tenant.
 *
 * Only accessible to owners and managers.
 *
 * @example
 * ```tsx
 * function TeamPage() {
 *   const { data: tenant } = useCurrentTenant();
 *   const { data: users } = useTenantUsers(tenant?.id);
 *
 *   return (
 *     <div>
 *       {users?.map(user => (
 *         <UserCard key={user.id} user={user} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useTenantUsers(tenantId?: string) {
  return useQuery({
    queryKey: [...TENANT_USERS_QUERY_KEY, tenantId],
    queryFn: () => fetchTenantUsers(tenantId!),
    enabled: !!tenantId,
    staleTime: 30000,
  });
}

/**
 * Hook to update the current tenant's profile.
 *
 * @example
 * ```tsx
 * function SettingsPage() {
 *   const updateTenant = useUpdateTenant();
 *
 *   const handleSave = (data) => {
 *     updateTenant.mutate({
 *       tenantId: tenant.id,
 *       updates: data,
 *     });
 *   };
 *
 *   return <SettingsForm onSave={handleSave} />;
 * }
 * ```
 */
export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      updates,
    }: {
      tenantId: string;
      updates: Partial<Omit<Tenant, "id" | "created_at" | "updated_at">>;
    }) => updateTenant(tenantId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEY });
      toast.success("Settings saved successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to save settings", {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to invite a new user to the tenant.
 */
export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      invitation,
    }: {
      tenantId: string;
      invitation: {
        email: string;
        name: string;
        role: TenantUserRole;
        phone?: string;
      };
    }) => inviteUserToTenant(tenantId, invitation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANT_USERS_QUERY_KEY });
      toast.success("Invitation sent successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to send invitation", {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to update a tenant user.
 * Includes tenant_id filter for proper multi-tenant isolation.
 */
export function useUpdateTenantUser() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: ({
      userId,
      updates,
    }: {
      userId: string;
      updates: Partial<Pick<TenantUser, "name" | "phone" | "role" | "permissions" | "is_active">>;
    }) => {
      if (!tenant?.id) {
        throw new Error("No tenant context available");
      }
      return updateTenantUser(tenant.id, userId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANT_USERS_QUERY_KEY });
      toast.success("User updated successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to update user", {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to remove a user from the tenant.
 * Includes tenant_id filter for proper multi-tenant isolation.
 */
export function useRemoveUser() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: (userId: string) => {
      if (!tenant?.id) {
        throw new Error("No tenant context available");
      }
      return removeUserFromTenant(tenant.id, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANT_USERS_QUERY_KEY });
      toast.success("User removed successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to remove user", {
        description: error.message,
      });
    },
  });
}

// ============================================================================
// Permission Helpers
// ============================================================================

const ROLE_HIERARCHY: Record<TenantUserRole, number> = {
  owner: 4,
  manager: 3,
  dispatcher: 2,
  technician: 1,
};

/**
 * Check if a role has at least the required permission level.
 *
 * @example
 * ```tsx
 * const canManageUsers = hasRoleOrHigher(user.role, 'manager');
 * ```
 */
export function hasRoleOrHigher(
  userRole: TenantUserRole,
  requiredRole: TenantUserRole
): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

/**
 * Check if user can perform a specific action.
 *
 * @example
 * ```tsx
 * const canEditCampaigns = canPerformAction(user, 'edit_campaigns');
 * ```
 */
export function canPerformAction(
  tenantUser: TenantUser,
  action: string
): boolean {
  // Check explicit permissions first
  if (tenantUser.permissions[action] !== undefined) {
    return tenantUser.permissions[action];
  }

  // Fall back to role-based permissions
  const rolePermissions: Record<TenantUserRole, string[]> = {
    owner: ["*"], // All permissions
    manager: [
      "view_dashboard",
      "view_calls",
      "view_jobs",
      "edit_jobs",
      "view_customers",
      "edit_customers",
      "view_technicians",
      "edit_technicians",
      "view_campaigns",
      "edit_campaigns",
      "view_analytics",
      "view_settings",
      "edit_settings",
    ],
    dispatcher: [
      "view_dashboard",
      "view_calls",
      "view_jobs",
      "edit_jobs",
      "view_customers",
      "view_technicians",
    ],
    technician: [
      "view_jobs",
      "edit_own_jobs",
      "view_customers",
      "add_observations",
    ],
  };

  const permissions = rolePermissions[tenantUser.role] || [];
  return permissions.includes("*") || permissions.includes(action);
}

/**
 * Hook to check if current user can perform an action.
 *
 * @example
 * ```tsx
 * function EditButton({ jobId }) {
 *   const canEdit = useCanPerformAction('edit_jobs');
 *
 *   if (!canEdit) return null;
 *
 *   return <Button onClick={() => editJob(jobId)}>Edit</Button>;
 * }
 * ```
 */
export function useCanPerformAction(action: string): boolean {
  const { data: tenant } = useCurrentTenant();

  if (!tenant?.tenant_user) return false;
  return canPerformAction(tenant.tenant_user, action);
}

/**
 * Hook to check if current user has a minimum role level.
 *
 * @example
 * ```tsx
 * function AdminPanel() {
 *   const isManager = useHasRole('manager');
 *
 *   if (!isManager) return <AccessDenied />;
 *
 *   return <AdminContent />;
 * }
 * ```
 */
export function useHasRole(requiredRole: TenantUserRole): boolean {
  const { data: tenant } = useCurrentTenant();

  if (!tenant?.tenant_user) return false;
  return hasRoleOrHigher(tenant.tenant_user.role, requiredRole);
}

// ============================================================================
// Subscription Tier Helpers
// ============================================================================

import { TIER_HIERARCHY, TIER_FEATURES, normalizeTierName, type SubscriptionTier, type TierFeature } from "@/lib/constants";

/**
 * Get the effective tier (handles null, legacy names, and new names)
 * Legacy mapping: receptionist→starter, outbound→growth, complete→dominator
 */
function getEffectiveTier(tier: string | null): SubscriptionTier {
  return normalizeTierName(tier);
}

/**
 * Check if a tier has access to a minimum tier level.
 */
export function hasTierOrHigher(
  currentTier: string | null,
  requiredTier: SubscriptionTier
): boolean {
  const effective = getEffectiveTier(currentTier);
  return TIER_HIERARCHY[effective] >= TIER_HIERARCHY[requiredTier];
}

/**
 * Check if a tier has access to a specific feature.
 */
export function hasTierFeature(
  currentTier: string | null,
  feature: TierFeature
): boolean {
  const effective = getEffectiveTier(currentTier);
  const features = TIER_FEATURES[effective];
  return features ? features[feature] === true : false;
}

/**
 * Get the max technicians allowed for a tier.
 */
export function getMaxTechnicians(currentTier: string | null): number {
  const effective = getEffectiveTier(currentTier);
  const features = TIER_FEATURES[effective];
  return features?.maxTechnicians ?? 0;
}

/**
 * Hook to check if current tenant has a minimum tier level.
 * Master accounts bypass all tier restrictions.
 *
 * @example
 * ```tsx
 * function TechPortal() {
 *   const hasTechAccess = useHasTier('outbound');
 *
 *   if (!hasTechAccess) return <UpgradePrompt tier="Professional" />;
 *
 *   return <TechPortalContent />;
 * }
 * ```
 */
export function useHasTier(requiredTier: SubscriptionTier): boolean {
  const { data: tenant } = useCurrentTenant();
  const { isMaster } = useAuth();

  // Master accounts have all tiers
  if (isMaster) return true;

  if (!tenant) return false;
  return hasTierOrHigher(tenant.subscription_tier, requiredTier);
}

/**
 * Hook to check if current tenant has access to a specific feature.
 * Master accounts bypass all feature restrictions.
 *
 * @example
 * ```tsx
 * function VoiceToTextButton() {
 *   const hasVoiceToText = useHasFeature('voiceToText');
 *
 *   if (!hasVoiceToText) return <UpgradePrompt feature="Voice-to-Text" />;
 *
 *   return <VoiceToTextRecorder />;
 * }
 * ```
 */
export function useHasFeature(feature: TierFeature): boolean {
  const { data: tenant } = useCurrentTenant();
  const { isMaster } = useAuth();

  // Master accounts have all features
  if (isMaster) return true;

  if (!tenant) return false;
  return hasTierFeature(tenant.subscription_tier, feature);
}

/**
 * Hook to get the current tenant's subscription tier info.
 * Master accounts get all features enabled.
 *
 * @example
 * ```tsx
 * function SubscriptionInfo() {
 *   const { tier, features, canUpgrade } = useSubscriptionTier();
 *
 *   return (
 *     <div>
 *       <p>Current tier: {tier}</p>
 *       <p>Tech Portal: {features.techPortal ? 'Enabled' : 'Upgrade required'}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useSubscriptionTier() {
  const { data: tenant, isLoading } = useCurrentTenant();
  const { isMaster } = useAuth();

  // Master accounts get dominator tier with all features
  if (isMaster) {
    const masterFeatures = TIER_FEATURES.dominator;
    return {
      tier: "dominator" as SubscriptionTier,
      tierLevel: TIER_HIERARCHY.dominator,
      features: { ...masterFeatures, maxTechnicians: 999 },
      maxTechnicians: 999,
      isLoading,
      isMaster: true,
      // Master accounts have all features
      hasTechPortal: true,
      hasVoiceToText: true,
      hasSmartDispatch: true,
      hasEquipmentSurvey: true,
      hasWebChat: true,
      hasSmsMarketing: true,
      hasReviewIntelligence: true,
      hasGoogle3Pack: true,
      // No upgrade needed
      canUpgradeTo: {
        growth: false,
        dominator: false,
        // Legacy aliases
        outbound: false,
        complete: false,
      },
    };
  }

  const effectiveTier = getEffectiveTier(tenant?.subscription_tier ?? null);
  const features = TIER_FEATURES[effectiveTier] || TIER_FEATURES.starter;
  const tierLevel = TIER_HIERARCHY[effectiveTier];

  return {
    tier: effectiveTier,
    tierLevel,
    features,
    maxTechnicians: features.maxTechnicians,
    isLoading,
    isMaster: false,
    // Convenience checks
    hasTechPortal: features.techPortal,
    hasVoiceToText: features.voiceToText,
    hasSmartDispatch: features.smartDispatch,
    hasEquipmentSurvey: features.equipmentSurvey,
    hasWebChat: features.webChat,
    hasSmsMarketing: features.smsMarketing,
    hasReviewIntelligence: features.reviewIntelligence,
    hasGoogle3Pack: features.google3Pack,
    // Upgrade paths
    canUpgradeTo: {
      growth: tierLevel < TIER_HIERARCHY.growth,
      dominator: tierLevel < TIER_HIERARCHY.dominator,
      // Legacy aliases
      outbound: tierLevel < TIER_HIERARCHY.growth,
      complete: tierLevel < TIER_HIERARCHY.dominator,
    },
  };
}
